import { CohortEnum } from '@eigtech/auth0-types'
import { Contact, GetBookResponse } from '@eigtech/contacts-types'
import {
  ChimeChannelMessageTypes,
  GetMeetingResponse,
  JoinMeetingRequest,
  StartMeetingRequest,
} from '@eigtech/meetings-types'
import { ensureJobCorn } from '@eigtech/restoration-util'
import { ensureClaimCorn } from '@eigtech/shared-corn-helper'
import { useQueryClient, UseQueryResult } from '@eigtech/ui-shared-api'
import { Center, CircularProgress, ComposedAlert } from '@eigtech/ui-shared-dave'
import { MeetingManager } from 'amazon-chime-sdk-component-library-react'
import { corrupt } from 'exhaustive'
import { useState } from 'react'
import { useMount } from 'react-use'
import { useShallow } from 'zustand/shallow'
import { getSdkTokenQueryOptions } from '../api/getSdkToken'
import { useJoinMeeting } from '../api/joinMeeting'
import { useStartMeeting } from '../api/startMeetings'
import { sendChannelMessage } from '../chime-sdk/chimeApi'
import {
  MeetingState,
  NonNullMeetingState,
  useMeetingStore,
  useMeetingStoreDangerously,
} from '../context/MeetingState'
import { ChimeMeeting, ChimeMeetingProps } from './ChimeMeeting'
import { JoinMeetingConfirmationModal } from './JoinMeetingConfirmationModal'
import { MeetingDetail } from './MeetingViews/MeetingDetail'
import { MeetingUploadRoute } from './MeetingViews/types'

export type EntityMeetingProps = {
  bookQuery: UseQueryResult<GetBookResponse>
  entityId: string
  entityType: 'claim' | 'job'
  cohort: CohortEnum
  getAttendee?: MeetingManager['getAttendee']
  meetingId: string | undefined
  meetingQuery: UseQueryResult<GetMeetingResponse>
  meetingStartTime: string | undefined
  meetingDescription?: string
  meetingUploadRoute?: MeetingUploadRoute
  navigateToMeeting: (props: { startTime: string; meetingId: string }) => void
} & Pick<Required<ChimeMeetingProps>, 'participant' | 'onLeaveMeeting'>

export function EntityMeeting({
  bookQuery,
  entityId,
  entityType,
  cohort,
  getAttendee,
  meetingId,
  meetingQuery,
  meetingStartTime,
  meetingUploadRoute,
  navigateToMeeting,
  participant,
  onLeaveMeeting,
}: EntityMeetingProps) {
  const entityCorn = (() => {
    switch (entityType) {
      case 'claim':
        return ensureClaimCorn(entityId)
      case 'job':
        return ensureJobCorn(entityId)

      default:
        corrupt(entityType)
    }
  })()

  const hasMeetingDetails = !!(entityCorn && meetingId && meetingStartTime)

  const {
    data: meeting,
    isPending: isPendingMeeting,
    isError: isMeetingError,
    refetch: refetchMeeting,
  } = meetingQuery

  const {
    data: book,
    isPending: isPendingBook,
    isError: isBookError,
    refetch: refetchBook,
  } = bookQuery

  useMount(() => {
    if (!hasMeetingDetails) return
    refetchMeeting()
    refetchBook()
  })

  const meetingDetails = useMeetingStore(useShallow(stateSelector))

  const [hasAgreedToJoin, setHasAgreedToJoin] = useState(false)

  if (isMeetingError || isBookError) {
    return <ComposedAlert alert="Could not retrieve meeting information." status="error" />
  }

  if (hasMeetingDetails && (isPendingMeeting || isPendingBook)) {
    return <MeetingLoader />
  }

  if (meeting?.status === 'closed' && !!(entityCorn && meetingId && meetingStartTime)) {
    return (
      <MeetingDetail
        cohort={cohort}
        entityId={entityCorn}
        meetingDescription={meeting.description}
        meetingId={meetingId}
        meetingUploadRoute={meetingUploadRoute}
        startTime={meetingStartTime}
      />
    )
  }

  if (
    hasMeetingDetails &&
    !!book &&
    meetingDetails?.entityId === entityCorn &&
    meetingDetails.meetingId === meetingId &&
    meetingDetails.startTime === meetingStartTime
  ) {
    return (
      <ReturnToMeeting
        getAttendee={getAttendee}
        invitableContacts={book.contacts}
        participant={participant}
        onLeaveMeeting={onLeaveMeeting}
      />
    )
  }

  if (!hasAgreedToJoin) {
    return (
      <JoinMeetingConfirmationModal
        open
        onCancel={onLeaveMeeting}
        onConfirm={() => setHasAgreedToJoin(true)}
        onOpenChange={() => {}}
      />
    )
  }

  if (hasMeetingDetails) {
    if (!(meeting && book)) {
      return <MeetingLoader />
    }

    return (
      <JoinMeeting
        entityId={entityCorn}
        getAttendee={getAttendee}
        invitableContacts={book.contacts}
        meetingId={meetingId}
        participant={participant}
        onLeaveMeeting={onLeaveMeeting}
      />
    )
  }

  return (
    <NewMeeting
      cohort={cohort}
      entityId={entityCorn}
      navigateToMeeting={navigateToMeeting}
      participant={participant}
    />
  )
}

function NewMeeting({
  navigateToMeeting,
  ...props
}: Omit<StartMeetingRequest, 'requestActor'> & Pick<EntityMeetingProps, 'navigateToMeeting'>) {
  const { mutateAsync: startMeeting } = useStartMeeting()
  const queryClient = useQueryClient()

  async function handleStartMeeting() {
    const meeting = await startMeeting(props)

    const credentials = await queryClient.fetchQuery(
      getSdkTokenQueryOptions({
        entityId: meeting.meetingDetails.entityId,
        startTime: meeting.meetingDetails.startTime,
      })
    )

    await sendChannelMessage(
      credentials,
      meeting.meetingDetails.appInstanceArn!,
      meeting.meetingDetails.chimeChannelArn!,
      props.participant.auth0Id!,
      props.participant.name,
      ChimeChannelMessageTypes.startMeeting
    )

    navigateToMeeting({
      startTime: meeting.meetingDetails.startTime,
      meetingId: meeting.meetingDetails.meetingId,
    })
  }

  useMount(() => {
    handleStartMeeting()
  })

  return <MeetingLoader />
}

function JoinMeeting(
  props: Omit<JoinMeetingRequest, 'requestActor'> &
    Pick<EntityMeetingProps, 'getAttendee' | 'onLeaveMeeting'> & {
      invitableContacts: Contact[]
    }
) {
  const { mutateAsync: joinMeeting, data, isPending } = useJoinMeeting()

  useMount(() => {
    joinMeeting(props)
  })

  return (
    <>
      {isPending || !data ? (
        <MeetingLoader />
      ) : (
        <ChimeMeeting
          {...props}
          canScreenshot
          chimeAttendee={data.chimeAttendee}
          chimeMeeting={data.chimeMeeting}
          meetingDetails={data.meetingDetails}
          participant={props.participant}
        />
      )}
    </>
  )
}

function ReturnToMeeting(
  props: Pick<JoinMeetingRequest, 'participant'> &
    Pick<EntityMeetingProps, 'getAttendee' | 'onLeaveMeeting'> & {
      invitableContacts: Contact[]
    }
) {
  const state = useMeetingStoreDangerously(useShallow(nonNullStateSelector))

  return <ChimeMeeting {...props} {...state} />
}

function MeetingLoader() {
  return (
    <Center py="12">
      <CircularProgress isIndeterminate size="100" />
    </Center>
  )
}

const stateSelector = ({ meetingDetails }: MeetingState) => meetingDetails

const nonNullStateSelector = ({
  chimeAttendee,
  chimeMeeting,
  meetingDetails,
}: NonNullMeetingState) => ({
  chimeAttendee,
  chimeMeeting,
  meetingDetails,
})
